import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FolderOpen, Plus, Search, Trash2, Pencil, Users, ChevronRight } from "lucide-react";
import advisorGroupService, {
  AdvisorGroupSummary,
  CreateGroupRequest,
} from "../../services/advisorGroupService";
import advisorClientService, {
  AdvisorClientListItem,
} from "../../services/advisorClientService";
import { useAuth } from "../../contexts/AuthContext";
import ConfirmationModal from "../../components/ConfirmationModal";

const AdvisorGroupsPage: React.FC = () => {
  const navigate = useNavigate();
  const { advisorAccountId: advisorAccountIdParam } = useParams<{ advisorAccountId: string }>();
  const advisorAccountId = Number(advisorAccountIdParam);
  const { user } = useAuth();

  const userRoles = user?.userRoles?.map((ur: { role: string }) => ur.role) || [];
  const hasAdvisorRole = userRoles.includes("ADVISOR");

  const [groups, setGroups] = useState<AdvisorGroupSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Create/edit modal state
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<AdvisorGroupSummary | null>(null);
  const [formName, setFormName] = useState("");
  const [formSelectedUserIds, setFormSelectedUserIds] = useState<Set<string>>(new Set());
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Client list for member picker
  const [allClients, setAllClients] = useState<AdvisorClientListItem[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [memberSearchQuery, setMemberSearchQuery] = useState("");

  // Edit modal: track whether the member preload has completed
  const [membersPreloaded, setMembersPreloaded] = useState(false);
  const [membersPreloadError, setMembersPreloadError] = useState(false);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState<AdvisorGroupSummary | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const groupList = hasAdvisorRole
        ? await advisorGroupService.listGroups()
        : await advisorGroupService.listGroupsAdmin(advisorAccountId);
      setGroups(groupList);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || "Failed to load groups";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [hasAdvisorRole, advisorAccountId]);

  const fetchClients = useCallback(async () => {
    try {
      setClientsLoading(true);
      let clientList: AdvisorClientListItem[];
      if (hasAdvisorRole) {
        ({ clients: clientList } = await advisorClientService.listClients());
      } else {
        ({ clients: clientList } = await advisorClientService.listClientsAdmin(advisorAccountId));
      }
      setAllClients(clientList);
    } catch {
      // Non-critical -- the member picker won't work but group CRUD still does
    } finally {
      setClientsLoading(false);
    }
  }, [hasAdvisorRole, advisorAccountId]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const openCreateModal = () => {
    setEditingGroup(null);
    setFormName("");
    setFormSelectedUserIds(new Set());
    setFormError(null);
    setMemberSearchQuery("");
    setMembersPreloaded(true);
    setMembersPreloadError(false);
    setShowFormModal(true);
    fetchClients();
  };

  const openEditModal = (group: AdvisorGroupSummary) => {
    setEditingGroup(group);
    setFormName(group.name);
    setFormSelectedUserIds(new Set());
    setFormError(null);
    setMemberSearchQuery("");
    setMembersPreloaded(false);
    setMembersPreloadError(false);
    setShowFormModal(true);
    fetchClients();

    const targetGroupId = group.id;
    const getGroupFn = hasAdvisorRole
      ? advisorGroupService.getGroup(group.id)
      : advisorGroupService.getGroupAdmin(advisorAccountId, group.id);
    getGroupFn.then((detail) => {
      setEditingGroup((current) => {
        if (current?.id !== targetGroupId) return current;
        setFormSelectedUserIds(new Set(detail.members.map((m) => m.userId)));
        setMembersPreloaded(true);
        return current;
      });
    }).catch(() => {
      setEditingGroup((current) => {
        if (current?.id !== targetGroupId) return current;
        setMembersPreloadError(true);
        return current;
      });
    });
  };

  const closeFormModal = () => {
    if (formLoading) return;
    setShowFormModal(false);
    setEditingGroup(null);
    setFormError(null);
  };

  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = formName.trim();
    if (!trimmedName) return;

    try {
      setFormLoading(true);
      setFormError(null);

      if (editingGroup) {
        const updatePayload = {
          name: trimmedName,
          memberUserIds: Array.from(formSelectedUserIds),
        };
        if (hasAdvisorRole) {
          await advisorGroupService.updateGroup(editingGroup.id, updatePayload);
        } else {
          await advisorGroupService.updateGroupAdmin(advisorAccountId, editingGroup.id, updatePayload);
        }
      } else {
        const payload: CreateGroupRequest = { name: trimmedName };
        if (formSelectedUserIds.size > 0) {
          payload.memberUserIds = Array.from(formSelectedUserIds);
        }
        if (hasAdvisorRole) {
          await advisorGroupService.createGroup(payload);
        } else {
          await advisorGroupService.createGroupAdmin(advisorAccountId, payload);
        }
      }

      closeFormModal();
      await fetchGroups();
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || "Failed to save group";
      setFormError(message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteLoading(true);
      if (hasAdvisorRole) {
        await advisorGroupService.deleteGroup(deleteTarget.id);
      } else {
        await advisorGroupService.deleteGroupAdmin(advisorAccountId, deleteTarget.id);
      }
      setDeleteTarget(null);
      await fetchGroups();
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || "Failed to delete group";
      setError(message);
      setDeleteTarget(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const toggleMember = (userId: string) => {
    setFormSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  // For create: show only ungrouped clients
  // For edit: show ungrouped + current group members
  const availableClients = allClients.filter((client) => {
    if (editingGroup) {
      return client.groupId === null || client.groupId === editingGroup.id;
    }
    return client.groupId === null;
  });

  const filteredAvailableClients = availableClients.filter((client) => {
    if (!memberSearchQuery) return true;
    const query = memberSearchQuery.toLowerCase();
    return (
      client.name?.toLowerCase().includes(query) ||
      client.email.toLowerCase().includes(query) ||
      client.ibkrAccountId?.toLowerCase().includes(query)
    );
  });

  const filteredGroups = groups.filter((group) => {
    if (!searchQuery) return true;
    return group.name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <div className="p-6 md:p-8 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-white">Groups</h1>
          <p className="text-sm text-gray-400 mt-1">
            Organize clients into groups and manage shared strategy templates.
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 px-4 py-2.5 bg-rp-amber hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors text-sm"
        >
          <Plus className="w-4 h-4" />
          Create Group
        </button>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            placeholder="Search groups..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-rp-surface-raised border border-gray-200 dark:border-rp-separator rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-rp-amber focus:ring-1 focus:ring-rp-amber transition-all"
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          <button
            onClick={fetchGroups}
            className="mt-2 text-sm text-red-600 dark:text-red-400 underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="animate-pulse bg-gray-200 dark:bg-gray-800 rounded-lg h-20" />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && filteredGroups.length === 0 && (
        <div className="py-24 bg-white dark:bg-rp-surface rounded-2xl border border-gray-200 dark:border-rp-separator flex flex-col items-center justify-center">
          <FolderOpen className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-4" />
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {searchQuery ? "No groups match your search." : "No groups yet. Create one to organize your clients."}
          </p>
          {!searchQuery && (
            <button
              onClick={openCreateModal}
              className="px-4 py-2.5 bg-rp-amber hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors text-sm"
            >
              Create Group
            </button>
          )}
        </div>
      )}

      {/* Group list */}
      {!loading && !error && filteredGroups.length > 0 && (
        <div className="bg-white dark:bg-rp-surface rounded-xl border border-gray-200 dark:border-rp-separator overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-gray-200 dark:border-rp-separator text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            <div className="col-span-4">Group Name</div>
            <div className="col-span-2">Members</div>
            <div className="col-span-2">Strategy</div>
            <div className="col-span-2">Created</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>

          {filteredGroups.map((group) => (
            <div
              key={group.id}
              className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-gray-200 dark:border-rp-separator last:border-b-0 hover:bg-gray-50 dark:hover:bg-rp-surface-raised transition-colors cursor-pointer"
              onClick={() => navigate(`/advisor/${advisorAccountId}/groups/${group.id}`)}
            >
              <div className="col-span-4 flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gray-100 dark:bg-rp-surface-raised shrink-0">
                  <FolderOpen className="w-4 h-4 text-rp-amber" />
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {group.name}
                </span>
              </div>
              <div className="col-span-2 flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  {group.memberCount}
                </span>
              </div>
              <div className="col-span-2 flex items-center">
                {group.hasStrategy ? (
                  <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-[10px] font-semibold uppercase tracking-wider rounded border border-green-200 dark:border-green-800">
                    Active
                  </span>
                ) : (
                  <span className="text-xs text-gray-400">Not set</span>
                )}
              </div>
              <div className="col-span-2 flex items-center">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {new Date(group.createdAtUtc).toLocaleDateString()}
                </span>
              </div>
              <div className="col-span-2 flex items-center justify-end gap-1">
                <button
                  onClick={(e) => { e.stopPropagation(); openEditModal(group); }}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-rp-amber hover:bg-rp-amber/10 transition-colors"
                  title="Edit group"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setDeleteTarget(group); }}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-500/10 transition-colors"
                  title="Delete group"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                <ChevronRight className="w-4 h-4 text-gray-400 ml-1" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Group Modal */}
      {showFormModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closeFormModal}
          />
          <div className="relative bg-white dark:bg-rp-surface-raised border border-gray-200 dark:border-rp-separator rounded-lg shadow-lg w-full max-w-lg mx-4 p-6 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {editingGroup ? "Edit Group" : "Create Group"}
              </h3>
            </div>

            <form onSubmit={handleSubmitForm} className="flex flex-col flex-1 min-h-0 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Group Name
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g. Conservative Portfolio"
                  className="w-full px-3 py-2.5 bg-white dark:bg-rp-surface-raised border border-gray-200 dark:border-rp-separator rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-rp-amber focus:ring-1 focus:ring-rp-amber transition-all"
                  required
                  autoFocus
                />
              </div>

              {/* Member picker */}
              <div className="flex-1 min-h-0 flex flex-col">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Members
                  {formSelectedUserIds.size > 0 && (
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                      ({formSelectedUserIds.size} selected)
                    </span>
                  )}
                </label>
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search clients..."
                    value={memberSearchQuery}
                    onChange={(e) => setMemberSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 bg-white dark:bg-rp-surface border border-gray-200 dark:border-rp-separator rounded-lg text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-rp-amber focus:ring-1 focus:ring-rp-amber transition-all"
                  />
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto border border-gray-200 dark:border-rp-separator rounded-lg">
                  {clientsLoading && (
                    <div className="p-4 text-center text-xs text-gray-400">Loading clients...</div>
                  )}
                  {!clientsLoading && filteredAvailableClients.length === 0 && (
                    <div className="p-4 text-center text-xs text-gray-400">
                      {availableClients.length === 0
                        ? "No ungrouped clients available."
                        : "No clients match your search."}
                    </div>
                  )}
                  {filteredAvailableClients.map((client) => (
                    <label
                      key={client.userId}
                      className="flex items-center gap-3 px-3 py-2.5 border-b border-gray-200 dark:border-rp-separator last:border-b-0 hover:bg-gray-50 dark:hover:bg-rp-surface cursor-pointer transition-colors"
                    >
                      <div
                        className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                          formSelectedUserIds.has(client.userId)
                            ? "bg-rp-amber border-rp-amber"
                            : "border-gray-300 dark:border-rp-separator hover:border-rp-amber"
                        }`}
                        onClick={() => toggleMember(client.userId)}
                      >
                        {formSelectedUserIds.has(client.userId) && (
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0" onClick={() => toggleMember(client.userId)}>
                        <p className="text-sm text-gray-900 dark:text-white truncate">
                          {client.name || "Unnamed"}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {client.email}
                          {client.ibkrAccountId && (
                            <span className="font-mono ml-2">{client.ibkrAccountId}</span>
                          )}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {membersPreloadError && editingGroup && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-700 dark:text-red-400">
                    Failed to load current members. Cannot save without knowing existing membership.
                  </p>
                </div>
              )}

              {formError && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-700 dark:text-red-400">{formError}</p>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeFormModal}
                  disabled={formLoading}
                  className="px-4 py-2.5 bg-white dark:bg-rp-surface border border-gray-200 dark:border-rp-separator text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600 rounded-lg transition-colors text-sm font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formLoading || !formName.trim() || (!!editingGroup && !membersPreloaded) || membersPreloadError}
                  className="px-4 py-2.5 bg-rp-amber hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {formLoading
                    ? (editingGroup ? "Saving..." : "Creating...")
                    : (!membersPreloaded && editingGroup)
                      ? "Loading members..."
                      : (editingGroup ? "Save Changes" : "Create Group")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={!!deleteTarget}
        onClose={() => { if (!deleteLoading) setDeleteTarget(null); }}
        onConfirm={handleDeleteGroup}
        title="Delete Group"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? Members will become ungrouped. This cannot be undone.`}
        confirmText="Delete"
        isDestructive
        loading={deleteLoading}
      />
    </div>
  );
};

export default AdvisorGroupsPage;
